/* eslint-disable indent */
import React, { useState } from 'react'
import { FaChartLine } from 'react-icons/fa'
import {
  StepCard,
  StepHeader,
  StepContent,
  ScrollableContent,
  StatCard,
  ProgressBar,
} from './ModelTrainingView.styles'
import { Model } from '../../../types/models/model'
import { KeyValuePair } from '../../../types/common'
import { Row, Col } from 'react-bootstrap'
import AccuracyMetricsChart from './AccuracyMetricsChart'
import ErrorDialog from '../../../components/modals/ErrorDialog'

// Define the structure of classification training data
interface ClassificationKeyValuePair {
  Key: string
  Value: number | string | ClassificationKeyValuePair[] | ClassificationTrainingJobEntry[]
}

// Type for the nested array structure in classification training data
type ClassificationTrainingJobEntry = ClassificationKeyValuePair[]

interface ClassificationTrainingProgressCardProps {
  model: Model | undefined
  gradient: string
}

const ClassificationTrainingProgressCard: React.FC<ClassificationTrainingProgressCardProps> = ({
  model,
  gradient,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const getElapsedTime = () => {
    // If training hasn't started, return 'Pending'
    if (!model?.train_started_at) return 'Pending'

    try {
      const startDate = new Date(model.train_started_at)

      // Check if the date is valid
      if (isNaN(startDate.getTime())) return 'Pending'

      // Use current date if train_ended_at is not set or is the special "0001" date
      const endDate =
        model.train_ended_at && model.train_ended_at !== '0001-01-01T00:00:00Z'
          ? new Date(model.train_ended_at)
          : new Date()

      // Calculate time difference
      const diffMs = endDate.getTime() - startDate.getTime()

      // Handle negative time difference (could happen if clocks are not synchronized)
      if (diffMs < 0) return '0 minutes'

      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMins / 60)

      if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMins % 60} minute${
          diffMins % 60 !== 1 ? 's' : ''
        }`
      }

      return `${diffMins} minute${diffMins !== 1 ? 's' : ''}`
    } catch (error) {
      setErrorMessage(
        error
          ? (error as any).message || 'Error calculating elapsed time'
          : 'An unexpected error occurred'
      )
      setShowError(true)
      return 'Pending'
    }
  }

  const getFormattedDate = (dateString?: string) => {
    if (!dateString) return 'Pending'

    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
  }

  const getTrainingStatus = () => {
    if (model?.training_stats?.classification?.training_progress === 1) {
      return 'Training Completed'
    }
    if (!model?.training_stats || !Array.isArray(model.training_stats)) {
      return 'Pending'
    }

    const trainingJobMetrics = model.training_stats.find(
      (item: KeyValuePair) => item.key === 'training_job_metrics'
    )

    if (!trainingJobMetrics?.Value || !Array.isArray(trainingJobMetrics.Value)) {
      return 'Pending'
    }

    const firstJobValue = trainingJobMetrics.Value[0]?.Value
    if (!firstJobValue || !Array.isArray(firstJobValue)) {
      return 'Pending'
    }

    const jobStatus =
      firstJobValue.find((item: KeyValuePair) => item.key === 'training_job_status')?.Value ||
      'unknown'

    switch (jobStatus.toLowerCase()) {
      case 'running':
        return 'In progress'
      case 'complete':
        return 'Completed'
      case 'failed':
        return 'Failed'
      case 'stopped':
        return 'Stopped'
      default:
        return jobStatus
    }
  }

  const getTrainingProgress = () => {
    const currentEpoch = getCurrentEpoch()
    const totalEpochs = getTotalEpochs()

    if (totalEpochs === 0) return 0
    const percent = Math.round((currentEpoch / totalEpochs) * 100)
    if (model?.state === 'TRAINING' && percent >= 100) {
      return 90
    }
    return percent
  }

  const getCurrentEpoch = () => {
    if (!model?.training_stats?.classification) {
      return 0
    }

    const { training_job_metrics } = model.training_stats.classification

    // Get the first (and likely only) job key
    const jobKey = Object.keys(training_job_metrics)[0]
    if (!jobKey) return 0

    const jobData = training_job_metrics[jobKey]
    if (!jobData?.training_job_entries || !Array.isArray(jobData.training_job_entries)) {
      return 0
    }

    // Get the last entry's epoch
    const lastEntry = jobData.training_job_entries[jobData.training_job_entries.length - 1]
    return (lastEntry?.epoch ?? 0) + 1
  }

  const getTotalEpochs = () => {
    try {
      const hyperparameters = model?.parameters?.hyperparameters as any
      const epochs = hyperparameters?.hyperparameters?.training?.epochs

      if (epochs && typeof epochs === 'object' && 'value' in epochs) {
        const value = epochs.value as unknown
        return typeof value === 'number' ? value : 0
      }

      return 0
    } catch (error) {
      setErrorMessage(
        error
          ? (error as any).message || 'Error getting total epochs'
          : 'An unexpected error occurred'
      )
      setShowError(true)
      return 0
    }
  }

  // Get the latest entry for displaying current metrics
  const getLatestEntry = () => {
    if (
      !model?.training_stats?.classification ||
      !Array.isArray(model.training_stats.classification)
    ) {
      return null
    }

    const trainingJobMetrics = model.training_stats.classification.find(
      (item: KeyValuePair) => item.key === 'training_job_metrics'
    )

    if (!trainingJobMetrics?.Value || !Array.isArray(trainingJobMetrics.Value)) {
      return null
    }

    const firstJobValue = trainingJobMetrics.Value[0]?.Value
    if (!firstJobValue || !Array.isArray(firstJobValue)) {
      return null
    }

    const jobEntries = firstJobValue.find(
      (item: KeyValuePair) => item.key === 'training_job_entries'
    )?.Value

    if (!jobEntries || !Array.isArray(jobEntries) || jobEntries.length === 0) {
      return null
    }

    const lastEntry = jobEntries[jobEntries.length - 1]
    if (!Array.isArray(lastEntry)) {
      return null
    }

    const accuracyEntry = lastEntry.find((item: KeyValuePair) => item.key === 'accuracy')

    return {
      accuracy: Number(accuracyEntry?.Value || 0),
    }
  }

  const latestEntry = getLatestEntry()

  return (
    <>
      <StepCard gradient={gradient}>
        <StepHeader $gradient={gradient}>
          <FaChartLine size={24} style={{ marginRight: '10px' }} />
          <span>{`Classification Training Progress${
            model?.training_stats?.classification?.training_progress === 1 ? ' - Completed' : ''
          }`}</span>
        </StepHeader>
        <StepContent>
          <ScrollableContent>
            {model?.training_stats ? (
              <>
                <Row className="mb-4">
                  <Col md={8}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#fff',
                        }}
                      >
                        {getTrainingStatus()}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#4dabf7' }}>
                        {getTrainingProgress()}%
                      </div>
                    </div>
                    <ProgressBar $progress={getTrainingProgress()} />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '0.5rem',
                        fontSize: '0.8rem',
                        color: '#aaa',
                      }}
                    >
                      <div>Started: {getFormattedDate(model?.train_started_at)}</div>
                      <div>Elapsed: {getElapsedTime()}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <StatCard
                      style={{
                        padding: '0.5rem',
                        margin: 0,
                        height: 'fit-content',
                      }}
                    >
                      <div className="stat-number" style={{ fontSize: '1.1rem', marginBottom: 0 }}>
                        {getCurrentEpoch()} / {getTotalEpochs()}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        Epochs
                      </div>
                    </StatCard>
                  </Col>
                </Row>

                {latestEntry && (
                  <Row className="mb-4">
                    <Col>
                      <StatCard>
                        <div className="stat-title">Current Accuracy</div>
                        <div className="stat-value">{(latestEntry.accuracy * 100).toFixed(2)}%</div>
                      </StatCard>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <AccuracyMetricsChart model={model} />
                  </Col>
                </Row>
              </>
            ) : (
              <Row className="h-100">
                <Col className="d-flex justify-content-center align-items-center text-muted">
                  No training data available
                </Col>
              </Row>
            )}
          </ScrollableContent>
        </StepContent>
      </StepCard>

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </>
  )
}

export default ClassificationTrainingProgressCard
